import React from "react";
import { isDateInFuture } from "@src/utils/date";
import { ReactElement } from "react";

/**
 * Converts a number (or string that can be converted to a number)
 * into currency-formatted string.
 * @param value The number or string to format
 * @returns The currency-formatted string
 */
export function formatMoney(
  value?: string | number | null,
  decimals = 2,
  notation: "compact" | "standard" | "scientific" | "engineering" = "standard",
  currency: string | null | undefined = "USD",
): string {
  // Convert to number
  const num = Number(value);
  if (isNaN(num)) {
    return "";
  }
  const formatter = Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency ?? "USD",
    maximumFractionDigits: decimals,
    currencySign: "accounting",
    signDisplay: "auto",
    notation,
  });
  return formatter.format(num);
}

export function moneyToNumber(money: string | number): number | null {
  if (typeof money === "number") {
    return money;
  }
  const reg = /\D*(\d+|\d.*?\d)(?:\D+(\d{2}))?\D*$/;
  const parts = reg.exec(money);
  if (parts && parts.length) {
    const number = parseFloat(
      parts[1].replace(/\D/, "") + "." + (parts[2] ? parts[2] : "00"),
    );
    return Number(number.toFixed(2));
  }
  return null;
}

// Max integer supported in Postgres.
// Used in place of infinity.
export const MAX_32_BIT_INT = Math.pow(2, 31) - 1;

export function formatAmountBasedOnDate({
  amount,
  date,
  output = "string",
}: {
  amount: number | undefined;
  date: string;
  output?: "string" | "html";
}): string | ReactElement {
  if (isDateInFuture(date) && (amount ?? 0) === 0) {
    return output === "html" ? <>&mdash;</> : "-";
  }

  return formatMoney(amount, 0);
}
